/**
 * LaunchCommandBar · Jun 2026
 * ================================================================
 * The Launch-mode sibling of <OperatingCommandBar />. Rendered by
 * <OperatingModeShell /> for businesses still in LAUNCHING mode so
 * pre-launch founders get the same always-visible action strip.
 *
 * 6 highest-frequency Launch actions (Jun 2026 founder-requested order):
 *   1. What's due     · /compliance?business={id}
 *   2. Log actuals    · /business/{id}/log-actuals
 *   3. Weekly Review  · /business/{id}/weekly-review
 *   4. Business Plan  · /business/{id}/plan
 *   5. Roadmap        · /business/{id}/roadmap
 *   6. Compliance     · /compliance?business={id}
 *
 * Right side: the composite Launch readiness score (lib/launchScore) —
 * tappable to open a breakdown panel showing what's holding it back —
 * and the same ⌘K quick-search trigger the Operating bar has.
 * Same chrome as the Operating bar: bottom/top position toggle and a
 * right-edge collapse handle — persisted under launch-scoped keys.
 */
import { Link, useLocation } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import {
  FileText,
  Map,
  DollarSign,
  ShieldCheck,
  Bell,
  GripVertical,
  ChevronsRight,
  ChevronsLeft,
  Rocket,
  Command,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { computeLaunchScore } from "../../lib/launchScore";
import { isCurrentLaunchRoute } from "../../lib/launchBarRoutes";
import { planLink } from "../../lib/planLabel";
import { useHubPrefs } from "../../lib/useHubPrefs";
import {
  epochWeek,
  recordReadiness,
  readinessDelta,
} from "../../lib/readinessTrend";
import { dueAttentionCount, roadmapDueAttentionCount, grantsDueAttentionCount } from "../DueSoonBanner";
import WhatsDuePanel from "./WhatsDuePanel";

const ITEMS = [
  {
    key: "whats-due",
    label: "What's due",
    icon: Bell,
    color: "#DC2626",
    toBuilder: (id) => `/compliance?business=${id}`,
  },
  {
    key: "log-actuals",
    label: "Log actuals",
    icon: DollarSign,
    color: "#10B981",
    toBuilder: (id) => `/business/${id}/log-actuals`,
  },
  // Feb 2027 · Weekly Review tile removed — the review now lives
  // inline on the Hub inside the "How am I doing?" section, so the
  // toolbar shortcut was redundant. Standalone route still resolves.
  {
    key: "business-plan",
    label: "Business Plan",
    icon: FileText,
    color: "#4A4458",
    // Jun 2026 · Honors the Default Business Plan View pref via
    // `planLink` — launching mode lands on the Overview (hierarchy)
    // by default, or the sectioned plan when the founder picked it.
    toBuilder: (id, hubPrefs) => planLink({ mode: "launching" }, id, hubPrefs),
  },
  {
    key: "roadmap",
    label: "Roadmap",
    icon: Map,
    color: "#2563EB",
    toBuilder: (id) => `/business/${id}/roadmap`,
  },
  {
    key: "compliance",
    label: "Compliance",
    icon: ShieldCheck,
    color: "#F59E0B",
    toBuilder: (id) => `/compliance?business=${id}`,
  },
];

const BREAKDOWN_LABELS = {
  health: "Plan health",
  compliance: "Compliance",
  roadmap: "Roadmap",
  runway: "Runway",
  goals: "Goals",
};

// Jun 2026 · Quick links — each "Holding it back" item jumps straight
// to the surface where the founder can fix it.
const MISSING_LINKS = {
  "Run plan health check": (id) => `/business/${id}/plan#section-health`,
  "Scan compliance": (id) => `/compliance?business=${id}`,
  "Generate roadmap": (id) => `/business/${id}/roadmap`,
  "Plan budget": (id) => `/business/${id}/plan#section-budget`,
  "Set business goals": (id) => `/business/${id}/plan#section-goals`,
};

export default function LaunchCommandBar({ businessId, business, onOpenPalette, onBusinessUpdate }) {
  const location = useLocation();
  const { hubPrefs } = useHubPrefs();
  const launch = business ? computeLaunchScore(business) : null;
  const score = launch?.score ?? null;
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  // Jun 2026 · Due-date nudges — the red count badge opens a panel to
  // snooze/reschedule due compliance items without leaving the page.
  const [duePanelOpen, setDuePanelOpen] = useState(false);
  // Jun 2026 · Readiness trend — record this week's score (per business,
  // localStorage) and surface the week-over-week delta.
  const [trend, setTrend] = useState([]);
  useEffect(() => {
    if (!businessId || score === null) return;
    setTrend(recordReadiness(businessId, epochWeek(), score));
  }, [businessId, score]);
  const delta = readinessDelta(trend);
  // Jun 2026 · What's-due badge — overdue + due-this-week open items.
  const dueCount = dueAttentionCount(business);
  // Jun 2026 · Roadmap nudges — badge also counts roadmap tasks due
  // this week / overdue so the panel's Roadmap section is reachable.
  const totalDueCount =
    dueCount + roadmapDueAttentionCount(business) + grantsDueAttentionCount(business);
  // Jun 2026 · Sparkline geometry — last 12 weekly scores mapped onto a
  // 240×48 viewBox (5px padding, y inverted so higher scores sit higher).
  const spark = trend.slice(-12);
  const sparkPoints = spark
    .map((e, i) => {
      const x = spark.length === 1 ? 120 : 5 + (i / (spark.length - 1)) * 230;
      const y = 43 - (Math.max(0, Math.min(100, e.score)) / 100) * 38;
      return `${Math.round(x)},${Math.round(y)}`;
    })
    .join(" ");
  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPod|iPad/i.test(navigator.platform || "");
  const kbdLabel = isMac ? "⌘K" : "Ctrl+K";

  const currentItem = ITEMS.find((it) =>
    isCurrentLaunchRoute(it, location.pathname, location.search, businessId),
  );
  const visibleItems = ITEMS.filter((it) => it !== currentItem);

  const [position, setPosition] = useState("bottom");
  useEffect(() => {
    try {
      const saved = localStorage.getItem("tlb:launch-bar-position");
      if (saved === "top" || saved === "bottom") setPosition(saved);
    } catch {
      /* no-op */
    }
  }, []);
  const togglePosition = useCallback(() => {
    setPosition((prev) => {
      const next = prev === "bottom" ? "top" : "bottom";
      try {
        localStorage.setItem("tlb:launch-bar-position", next);
      } catch {
        /* no-op */
      }
      return next;
    });
  }, []);
  const desktopPositionClass =
    position === "top"
      ? "fixed top-16 left-1/2 -translate-x-1/2 z-30"
      : "fixed bottom-6 left-1/2 -translate-x-1/2 z-30";

  const isMobileViewport = () => {
    try {
      return !window.matchMedia("(min-width: 768px)").matches;
    } catch {
      return false;
    }
  };
  const collapseKey = (mobile) =>
    mobile ? "tlb:launch-bar-collapsed:mobile" : "tlb:launch-bar-collapsed:desktop";
  const [mobileViewport, setMobileViewport] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    const readForViewport = (mobile) => {
      try {
        return localStorage.getItem(collapseKey(mobile)) === "1";
      } catch {
        return false;
      }
    };
    const mobile = isMobileViewport();
    setMobileViewport(mobile);
    setCollapsed(readForViewport(mobile));
    let mq;
    try {
      mq = window.matchMedia("(min-width: 768px)");
    } catch {
      return undefined;
    }
    const onChange = (e) => {
      const nowMobile = !e.matches;
      setMobileViewport(nowMobile);
      setCollapsed(readForViewport(nowMobile));
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(collapseKey(mobileViewport), next ? "1" : "0");
      } catch {
        /* no-op */
      }
      return next;
    });
  }, [mobileViewport]);

  const renderItem = (item, testid) => {
    const Icon = item.icon;
    return (
      <Link
        key={item.key}
        to={item.toBuilder(businessId, hubPrefs)}
        className="group inline-flex items-center gap-2 px-3 py-2 border border-transparent hover:border-[#4A4458] hover:bg-white transition-colors"
        style={{ borderRadius: 0 }}
        data-testid={testid}
      >
        <span
          className="relative w-6 h-6 flex items-center justify-center text-white"
          style={{ background: item.color }}
        >
          <Icon className="w-3.5 h-3.5" aria-hidden />
          {item.key === "whats-due" && totalDueCount > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDuePanelOpen((v) => !v);
              }}
              className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 flex items-center justify-center bg-[#DC2626] hover:bg-[#B91C1C] text-white text-[9px] font-black leading-none cursor-pointer"
              data-testid="launch-whats-due-count"
              aria-label={`${totalDueCount} item${totalDueCount === 1 ? "" : "s"} due or overdue — tap to snooze or reschedule`}
              title="Snooze or reschedule due items"
            >
              {totalDueCount > 9 ? "9+" : totalDueCount}
            </button>
          )}
        </span>
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#4A4458] whitespace-nowrap">
          {item.label}
        </span>
      </Link>
    );
  };

  const renderHerePill = (testid) => {
    if (!currentItem) return null;
    const HereIcon = currentItem.icon;
    return (
      <span
        className="inline-flex items-center gap-2 px-3 py-2 border shrink-0"
        style={{ borderRadius: 0, borderColor: currentItem.color, background: "#F9FAFB" }}
        data-testid={testid}
        data-here-key={currentItem.key}
        aria-label={`You are here: ${currentItem.label}`}
      >
        <span
          className="w-6 h-6 flex items-center justify-center text-white"
          style={{ background: currentItem.color }}
          aria-hidden
        >
          <HereIcon className="w-3.5 h-3.5" />
        </span>
        <span
          className="text-[11px] font-black uppercase tracking-[0.16em] whitespace-nowrap"
          style={{ color: currentItem.color }}
        >
          Here · {currentItem.label}
        </span>
      </span>
    );
  };

  return (
    <>
      {collapsed && (
        <button
          type="button"
          onClick={toggleCollapsed}
          className={`hidden md:inline-flex ${
            position === "top" ? "fixed top-6 right-6 z-30" : "fixed bottom-24 right-6 z-30"
          } group items-center px-2 h-12 bg-white hover:bg-neutral-900 text-neutral-900 hover:text-white border-2 border-neutral-900 shadow-[6px_6px_0_0_rgba(0,0,0,0.18)] hover:shadow-[8px_8px_0_0_rgba(0,0,0,0.24)] transition-colors rounded-none`}
          data-testid="launch-command-bar-collapsed-handle"
          aria-label="Expand launch command bar"
          title="Expand launch command bar"
        >
          <span className="w-7 h-7 flex items-center justify-center bg-[#D97706] text-white flex-shrink-0">
            <ChevronsLeft className="w-4 h-4" aria-hidden />
          </span>
          <span className="max-w-0 opacity-0 ml-0 group-hover:max-w-[120px] group-hover:opacity-100 group-hover:ml-2 group-hover:mr-1 overflow-hidden whitespace-nowrap transition-[max-width,opacity,margin] duration-300 ease-out text-xs uppercase tracking-[0.25em] font-bold">
            Launch
          </span>
        </button>
      )}
      {collapsed && (
        <button
          type="button"
          onClick={toggleCollapsed}
          className="md:hidden fixed bottom-40 right-6 z-30 inline-flex items-center gap-2 h-10 px-2 bg-white text-neutral-900 border-2 border-[#4A4458] shadow-[4px_4px_0px_rgba(74,68,88,1)] active:translate-y-0.5 transition-transform"
          style={{ bottom: "calc(10rem + env(safe-area-inset-bottom))", borderRadius: 0 }}
          data-testid="launch-command-bar-collapsed-handle-mobile"
          aria-label="Expand launch command bar"
          title="Expand launch command bar"
        >
          <span className="w-6 h-6 flex items-center justify-center bg-[#D97706] text-white flex-shrink-0">
            <ChevronsLeft className="w-3.5 h-3.5" aria-hidden />
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] font-black text-[#4A4458]">
            Launch
          </span>
        </button>
      )}

      {/* Desktop pill */}
      <div
        className={`${collapsed ? "hidden" : "hidden md:flex"} ${desktopPositionClass} max-w-[calc(100vw-14rem)] items-center gap-3 border border-[#4A4458] bg-white shadow-[4px_4px_0px_rgba(74,68,88,1)] px-3 py-2`}
        style={{ borderRadius: 0 }}
        data-testid="launch-command-bar"
        data-position={position}
        data-collapsed={collapsed ? "true" : "false"}
        aria-label="Launch command bar"
      >
        <button
          type="button"
          onClick={togglePosition}
          className="shrink-0 p-1 text-neutral-500 hover:text-[#4A4458] hover:bg-neutral-100 transition-colors"
          title={position === "bottom" ? "Snap bar to top of screen" : "Snap bar to bottom of screen"}
          data-testid="launch-command-bar-toggle-position"
          aria-label="Toggle bar position"
        >
          <GripVertical className="w-3.5 h-3.5" aria-hidden />
        </button>
        <button
          type="button"
          onClick={toggleCollapsed}
          className="shrink-0 p-1 text-neutral-500 hover:text-[#4A4458] hover:bg-neutral-100 transition-colors"
          title="Collapse launch command bar to the right"
          data-testid="launch-command-bar-toggle-collapse"
          aria-label="Collapse launch command bar"
        >
          <ChevronsRight className="w-3.5 h-3.5" aria-hidden />
        </button>
        <span className="text-[10px] font-black uppercase tracking-[0.24em] text-[#D97706] whitespace-nowrap pl-1 shrink-0">
          Launch
        </span>
        <span className="w-px h-6 bg-neutral-200 shrink-0" aria-hidden />
        <div className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto">
          {renderHerePill("launch-command-bar-here")}
          {visibleItems.map((it) => renderItem(it, `launch-action-${it.key}`))}
        </div>
        <span className="w-px h-6 bg-neutral-200 shrink-0" aria-hidden />
        <div className="flex items-center gap-3 shrink-0">
          {score !== null && (
            <button
              type="button"
              onClick={() => setBreakdownOpen((v) => !v)}
              className="hidden lg:inline-flex items-center gap-2 px-2 py-1 hover:bg-neutral-100 transition-colors"
              data-testid="launch-command-bar-score"
              aria-expanded={breakdownOpen}
              title={`Launch readiness · ${score}/100 — tap to see what's holding it back`}
            >
              <Rocket className="w-3.5 h-3.5 text-[#D97706]" aria-hidden />
              <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#4A4458]">
                Readiness
              </span>
              <span className="text-sm font-black tabular-nums text-[#D97706]">{score}</span>
              {delta !== null && delta !== 0 && (
                <span
                  className={`inline-flex items-center gap-0.5 text-[10px] font-black tabular-nums ${delta > 0 ? "text-emerald-600" : "text-rose-600"}`}
                  data-testid="launch-readiness-delta"
                  title={`${delta > 0 ? "+" : ""}${delta} vs last week`}
                >
                  {delta > 0 ? <TrendingUp className="w-3 h-3" aria-hidden /> : <TrendingDown className="w-3 h-3" aria-hidden />}
                  {delta > 0 ? `+${delta}` : delta}
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={onOpenPalette}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#4A4458] bg-white hover:bg-neutral-100 transition-colors"
            style={{ borderRadius: 0 }}
            data-testid="launch-command-palette-trigger"
            aria-label="Open command palette"
          >
            <Command className="w-3.5 h-3.5 text-[#D97706]" aria-hidden />
            <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#4A4458]">
              Search
            </span>
            <kbd className="text-[10px] font-mono text-neutral-500 border border-neutral-300 px-1">
              {kbdLabel}
            </kbd>
          </button>
        </div>
        {/* Jun 2026 · Readiness breakdown — what's holding the score back. */}
        {breakdownOpen && launch && (
          <div
            className={`absolute right-0 ${position === "top" ? "top-full mt-2" : "bottom-full mb-2"} w-80 border border-[#4A4458] bg-white shadow-[4px_4px_0px_rgba(74,68,88,1)] p-4 text-left`}
            style={{ borderRadius: 0 }}
            data-testid="launch-readiness-breakdown"
          >
            <div className="text-[10px] font-black uppercase tracking-[0.24em] text-[#D97706]">
              Launch readiness · {score}/100
            </div>
            {/* Jun 2026 · Week-over-week trend */}
            {trend.length > 0 && (
              <div className="mt-1 text-[11px] text-neutral-600" data-testid="launch-readiness-trend">
                {delta === null ? (
                  "Trend starts next week — this week's score is saved."
                ) : delta === 0 ? (
                  "Flat vs last week."
                ) : (
                  <span className={delta > 0 ? "text-emerald-700 font-bold" : "text-rose-700 font-bold"}>
                    {delta > 0 ? "▲ Up" : "▼ Down"} {Math.abs(delta)} vs last week
                    {` (${trend[trend.length - 2].score} → ${trend[trend.length - 1].score})`}
                  </span>
                )}
              </div>
            )}
            {/* Jun 2026 · Sparkline — last 12 weekly readiness scores. */}
            {spark.length >= 2 && (
              <svg
                viewBox="0 0 240 48"
                className="mt-2 w-full h-12"
                data-testid="launch-readiness-sparkline"
                role="img"
                aria-label={`Readiness score over the last ${spark.length} weeks`}
              >
                <line x1="5" y1="43" x2="235" y2="43" stroke="#E5E5E5" strokeWidth="1" />
                <polyline fill="none" stroke="#D97706" strokeWidth="2" points={sparkPoints} />
                {(() => {
                  const [lx, ly] = sparkPoints.split(" ").pop().split(",");
                  return <circle cx={lx} cy={ly} r="3" fill="#D97706" />;
                })()}
              </svg>
            )}
            <ul className="mt-3 space-y-1.5">
              {Object.entries(BREAKDOWN_LABELS).map(([k, label]) => {
                const v = launch.breakdown?.[k];
                if (v === null || v === undefined) return null;
                return (
                  <li
                    key={k}
                    className="flex items-center justify-between gap-3"
                    data-testid={`launch-breakdown-${k}`}
                  >
                    <span className="text-xs font-bold text-[#4A4458]">{label}</span>
                    <span className="flex items-center gap-2">
                      <span className="w-24 h-1.5 bg-neutral-200">
                        <span
                          className="block h-1.5 bg-[#D97706]"
                          style={{ width: `${Math.max(0, Math.min(100, v))}%` }}
                        />
                      </span>
                      <span className="text-xs font-black tabular-nums text-neutral-700 w-8 text-right">
                        {Math.round(v)}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
            {launch.missing?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-neutral-200" data-testid="launch-breakdown-missing">
                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-neutral-500">
                  Holding it back
                </div>
                <ul className="mt-1.5 space-y-1">
                  {launch.missing.map((m, i) => {
                    const toBuilder = MISSING_LINKS[m];
                    return (
                      <li key={i} className="text-xs text-neutral-700 flex items-start gap-1.5">
                        <span className="text-[#D97706] font-black" aria-hidden>·</span>
                        {toBuilder ? (
                          <Link
                            to={toBuilder(businessId)}
                            onClick={() => setBreakdownOpen(false)}
                            className="underline decoration-dotted underline-offset-2 hover:text-[#D97706] transition-colors"
                            data-testid={`launch-breakdown-fix-${i}`}
                          >
                            {m}
                          </Link>
                        ) : (
                          m
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
        {duePanelOpen && (
          <WhatsDuePanel
            business={business}
            businessId={businessId}
            onClose={() => setDuePanelOpen(false)}
            onBusinessUpdate={onBusinessUpdate}
            positionClass={position === "top" ? "left-0 top-full mt-2" : "left-0 bottom-full mb-2"}
          />
        )}
      </div>

      {/* Mobile pill */}
      <div
        className={`${collapsed ? "hidden" : "md:hidden flex"} fixed left-1/2 -translate-x-1/2 bottom-40 z-30 border border-[#4A4458] bg-white shadow-[4px_4px_0px_rgba(74,68,88,1)] max-w-[calc(100vw-1rem)]`}
        style={{ borderRadius: 0, bottom: "calc(10rem + env(safe-area-inset-bottom))" }}
        data-testid="launch-command-bar-mobile"
        data-collapsed={collapsed ? "true" : "false"}
        aria-label="Launch command bar (mobile)"
      >
        {duePanelOpen && (
          <WhatsDuePanel
            business={business}
            businessId={businessId}
            onClose={() => setDuePanelOpen(false)}
            onBusinessUpdate={onBusinessUpdate}
            positionClass="left-0 bottom-full mb-2"
          />
        )}
        <div className="flex items-center gap-1 overflow-x-auto px-2 py-2">
          {renderHerePill("launch-command-bar-here-mobile")}
          {visibleItems.map((it) => renderItem(it, `launch-action-mobile-${it.key}`))}
          <button
            type="button"
            onClick={onOpenPalette}
            className="inline-flex items-center gap-1 px-2 py-2 border border-[#4A4458] bg-white shrink-0"
            style={{ borderRadius: 0 }}
            data-testid="launch-command-palette-trigger-mobile"
            aria-label="Open command palette"
          >
            <Command className="w-3.5 h-3.5 text-[#D97706]" aria-hidden />
            <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#4A4458]">
              {kbdLabel}
            </span>
          </button>
          <button
            type="button"
            onClick={toggleCollapsed}
            className="shrink-0 p-2 text-neutral-500 hover:text-[#4A4458] hover:bg-neutral-100 transition-colors border-l border-neutral-200"
            data-testid="launch-command-bar-toggle-collapse-mobile"
            aria-label="Collapse launch command bar"
            title="Collapse launch command bar"
          >
            <ChevronsRight className="w-3.5 h-3.5" aria-hidden />
          </button>
        </div>
      </div>
    </>
  );
}
